import {ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {
    delay,
    DetectMethodChanges,
    findAtmStrikeIndex,
    isNullOrUndefined,
    isOptionExpired,
    isValidNumber,
    isVoid, makeGuiFriendlyExpirationDate, makePortfolioKey
} from "../../../utils";
import {TradingInstrument} from "../../../trading-instruments/trading-instrument.class";
import {ApgPortfolio} from "../../../adjustment-pricing-grid/model/ApgPortfolio";
import {OptionsChainService} from "../../../option-chains.service";
import {HedgePositionModel} from "./hedge-position.model";
import {HedgePositionsService} from "./hedge-positions.service";
import {UserSettingsService} from "../../../user-settings.service";
import {TradingInstrumentsService} from "../../../trading-instruments/trading-instruments-service.interface";
import * as Enumerable from "linq";
import {
    AfterStatePreviewRow,
    HgAfterStatePreviewComponent
} from "../after-state-preview/hg-after-state-preview.component";
import {MessageBusService} from "../../../message-bus.service";
import {filter, takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";
import {HgOptionStrategyService, HgOptionStrategyConfig} from "../../hg-option-strategy.service";
import {ApplicationSettingsService} from "../../../app-settings/application-settings.service";
import {parseOptionTicker} from "../../../options-common/options.model";
import {LastQuoteCacheService} from "../../../last-quote-cache.service";
import {HedgePosition} from "../../data-model/hedge-position";
import {HgOriginalClientPositionsService} from "../portfolio-selector/hg-original-client-positions.service";
import {ToastrService} from "ngx-toastr";
import {
    GenericConfirmationDialogComponent
} from "../../../generic-confirmation-dialog/generic-confirmation-dialog.component";
import {ApgDataService} from "../../../adjustment-pricing-grid/services/apg-data.service";
import {TradingInstrumentDisplayNameService} from "../../../trading-instruments/trading-instrument-display-name.service";
import {LegsGroup} from "../../settings-section/hg-settings-section.component";
import {HEDGE_BOOK_RESET_TOPIC, HedgeBookResetPayload} from "../../hg-hedge-book-reset.message";

interface HedgeGroup {
    groupLabel: string;
    groupColor: string;
    legs: HedgePositionModel[];
}

@Component({
    selector: 'ets-hg-hedge-positions',
    templateUrl: 'hg-hedge-positions.component.html',
    styleUrls: [
        './hg-hedge-positions.component.scss',
        '../../hedging-grid-common-styles.scss'
    ]
})

export class HgHedgePositionsComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _hedgePositionsService: HedgePositionsService,
        private readonly _tiService: TradingInstrumentsService,
        private readonly _messageBus: MessageBusService,
        private readonly _optionChainsService: OptionsChainService,
        private readonly _hgOptionStrategyService: HgOptionStrategyService,
        private readonly _apgDataService: ApgDataService,
        private readonly _appSettingsService: ApplicationSettingsService,
        private readonly _lastQuoteCache: LastQuoteCacheService,
        private readonly _originalPositions: HgOriginalClientPositionsService,
        private readonly _toastr: ToastrService,
        private readonly _userSettingsService: UserSettingsService,
        private readonly _displayNameService: TradingInstrumentDisplayNameService
    ) {
    }

    private _unsubscriber = new Subject();
    private _callLegsByGroup = undefined;
    private _putLegsByGroup = undefined;
    private _selectedPortfolio: ApgPortfolio;

    tradingInstrument: TradingInstrument;

    // Right-pinned badge on the "Hedge Positions" caption. Portfolio BASE underlying
    // (same resolver as the section headers), NOT the leg underlying. Null => no badge.
    underlyingBadge: string;

    hedgePositions: HedgePositionModel[] = [];

    @ViewChild(HgAfterStatePreviewComponent)
    afterStatePreviewCmp: HgAfterStatePreviewComponent;

    isLoading: boolean;

    esHedgingEnabled = false;

    @ViewChild(GenericConfirmationDialogComponent)
    confirmationDialog: GenericConfirmationDialogComponent;

    get hasPortfolio(): boolean {
        return !isVoid(this._selectedPortfolio);
    }

    get hasLegs(): boolean {
        return this.hasPortfolio && !isVoid(this.hedgePositions);
    }

    get hasCalls(): boolean {
        return this.hasPortfolio && this.hedgePositions.some(x => x.type === 'Call');
    }

    get hasPuts(): boolean {
        return this.hasPortfolio && this.hedgePositions.some(x => x.type === 'Put');
    }

    ngOnInit() {

        this._messageBus.of<{ enabled: boolean }>('Hg.EsHedgingModeChanged')
            .pipe(
                takeUntil(this._unsubscriber)
            )
            .subscribe(msg => {
                this.esHedgingEnabled = msg.payload.enabled;
                this._changeDetector.detectChanges();
            });

        this._messageBus.of<{ portfolio: ApgPortfolio }>('Hg.AfterStateUpdated')
            .pipe(
                filter(x => {
                    const pfKey0 = makePortfolioKey(x.payload.portfolio);
                    const pfKey1 = makePortfolioKey(this._selectedPortfolio);
                    return pfKey0 === pfKey1;
                }),
                takeUntil(this._unsubscriber)
            )
            .subscribe(x => this._changeDetector.detectChanges());

        this._messageBus.of<{
            positions: HedgePosition[],
            slot: 'first' | 'second',
            portfolio: ApgPortfolio
        }>('Hg.SaveAfterState')
            .pipe(
                filter(x => {
                    const pfKey0 = makePortfolioKey(x.payload.portfolio);
                    const pfKey1 = makePortfolioKey(this._selectedPortfolio);
                    return pfKey0 === pfKey1;
                }),
                takeUntil(this._unsubscriber)
            )
            .subscribe((x) => {
                this._hedgePositionsService.saveAfterState(
                    x.payload.positions,
                    x.payload.slot,
                    this._selectedPortfolio
                );
            });

        this._messageBus.of<{ hedgeId: string, hedgeName: string, side: 'Call' | 'Put' }>('Hg.RequestDeleteHedge')
            .pipe(
                takeUntil(this._unsubscriber)
            )
            .subscribe(msg => {
                this.deleteHedgeByRequest(msg.payload);
            });

        this._messageBus.of<{ portfolio: ApgPortfolio, positions: HedgePositionModel[] }>('Hg.SyncPositions')
            .pipe(
                filter(x => {
                    const pfKey0 = makePortfolioKey(x.payload.portfolio);
                    const pfKey1 = makePortfolioKey(this._selectedPortfolio);
                    return pfKey0 === pfKey1;
                }),
                takeUntil(this._unsubscriber)
            )
            .subscribe(x => this.onSyncPositions(x.payload));

        this._messageBus.of<{ leg: HedgePositionModel, portfolio: ApgPortfolio }>('Hg.RemoveExpiredLeg')
            .pipe(
                filter(x => {
                    const pfKey0 = makePortfolioKey(x.payload.portfolio);
                    const pfKey1 = makePortfolioKey(this._selectedPortfolio);
                    return pfKey0 === pfKey1;
                }),
                takeUntil(this._unsubscriber)
            ).subscribe(x => this.removeExpiredLeg(x.payload.leg));

        this._messageBus.of<{ state: 'former' | 'current', portfolio: ApgPortfolio }>('Hg.RefreshClientPositions')
            .pipe(
                filter(x => {
                    const pfKey0 = makePortfolioKey(x.payload.portfolio);
                    const pfKey1 = makePortfolioKey(this._selectedPortfolio);
                    return pfKey0 === pfKey1;
                }),
                takeUntil(this._unsubscriber)
            )
            .subscribe(async x => {
                try {
                    this._messageBus.publish({
                        topic: 'Hg.Loading',
                        payload: {isLoading: true}
                    });

                    await this.onRefreshClientPositions(x.payload);

                } finally {
                    this._messageBus.publish({
                        topic: 'Hg.Loading',
                        payload: {isLoading: false}
                    });
                    this._changeDetector.detectChanges();
                }
            });

        this._messageBus.of<{ ticker?: string }>('Qg.ChainsUpdatedMessage')
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(message => {
                this.refreshExpirationMetadata(message.payload?.ticker);
                this._changeDetector.detectChanges();
            });
    }

    private refreshExpirationMetadata(ticker?: string): void {
        // The replay date can make an expired held contract valid again. Rebind
        // its metadata, rather than reloading holdings or treating it as an edit.
        for (const leg of this.hedgePositions) {
            const contract = parseOptionTicker(leg.getTicker() || leg.expiredTicker);
            if (!contract || (ticker && contract.underlying !== ticker)
                || !this._optionChainsService.isChainConfirmed(contract.underlying)) {
                continue;
            }
            const chain = this._optionChainsService.getCachedChain(contract.underlying);
            const expiration = chain?.expirations.find(x =>
                x.underlyingTicker === contract.underlying
                && x.expirationTicker === contract.expirationTicker
                && x.optionExpirationDate === contract.expiration);
            if (!expiration) {
                continue;
            }
            leg.expirationList = chain.expirations.slice();
            leg.expirationDescriptor = expiration;
        }
    }

    ngOnDestroy() {
        this._unsubscriber.next();
        this._unsubscriber.complete();
    }

    getLegGroups(side: 'Call' | 'Put'): { groupLabel: string, groupColor: string, legs: HedgePositionModel[] }[] {

        if (side !== 'Call' && side !== 'Put') {
            return;
        }

        let groupField = side === 'Call' ? this._callLegsByGroup : this._putLegsByGroup;

        if (isVoid(groupField)) {
            const grps = Enumerable.from(this.hedgePositions)
                .where(x => x.type === side)
                .groupBy(x => x.groupId)
                .select(x => {
                    const legs = x.toArray();
                    if (true) {
                        legs.sort((a, b) => {
                            const bStrike = typeof b.strike === 'string' ? parseFloat(b.strike) : b.strike;
                            const aStrike = typeof a.strike === 'string' ? parseFloat(a.strike) : a.strike;

                            return bStrike - aStrike;
                        });
                    }
                    const ulTicker = legs[0].underlying;
                    const ulDisplayName = this._tiService.getInstrumentByTicker(ulTicker)?.displayName || ulTicker;
                    return {
                        groupLabel: legs[0].label,
                        groupColor: legs[0].color,
                        legs: legs,
                        groupOrder: legs[0].groupOrder,
                        underlying: ulTicker,
                        underlyingDisplayName: ulDisplayName
                    };
                }).toArray();

            const sorted = grps
                .sort((grpA, grpB) => {

                    const grpAOrder = grpA.legs[0].groupOrder;
                    const grpBOrder = grpB.legs[0].groupOrder;

                    if (isValidNumber(grpAOrder) && isValidNumber(grpBOrder)) {
                        return grpAOrder - grpBOrder;
                    } else {
                        const lastStrikeA = grpA.legs[grpA.legs.length - 1].strike;
                        const lastStrikeB = grpB.legs[grpB.legs.length - 1].strike;
                        return lastStrikeB - lastStrikeA;
                    }
                });

            if (side === 'Call') {
                this._callLegsByGroup = groupField = sorted;
            } else {
                this._putLegsByGroup = groupField = sorted;
            }
        }

        return groupField;
    }

    getExpirationColorRank(data, position: any) {
        return undefined;
    }

    @DetectMethodChanges({isAsync: true})
    async onPortfolioSelected(args: ApgPortfolio) {

        this._selectedPortfolio = args;

        try {
            // Same portfolio-base resolver the section headers use, so the caption
            // badge always matches them (independent book => its fixed asset).
            const underlyingTicker = await this._apgDataService.getUnderlyingOfPortfolio(args);
            this.underlyingBadge = isVoid(underlyingTicker)
                ? null
                : this._displayNameService.getDisplayNameForTicker(underlyingTicker);
        } catch {
            this.underlyingBadge = null;
        }

        const hedgePositionModels = await this._hedgePositionsService
            .getPositionModels(args);

        if (!isVoid(hedgePositionModels)) {
            const underlying = hedgePositionModels[0].underlying;
            if (!isVoid(underlying)) {
                this.tradingInstrument = this._tiService.getInstrumentByTicker(underlying);
            }
        }

        this.hedgePositions = hedgePositionModels;

        this.refreshLegs('Calls & Puts');
    }

    @DetectMethodChanges({isAsync: true})
    async showAfterStatePreview(slot: 'first' | 'second') {

        if (isVoid(this._selectedPortfolio)) {
            return;
        }

        const afterStatePositions = this._hedgePositionsService
            .getAfterState(slot, this._selectedPortfolio);

        if (isNullOrUndefined(afterStatePositions)) {
            return;
        }

        let side: 'Calls' | 'Puts';

        if (!isVoid(afterStatePositions)) {
            side = afterStatePositions.some(x => x.type === 'Call')
                ? 'Calls'
                : 'Puts';
        } else {
            side = slot === 'second' ? 'Puts' : 'Calls';
        }

        let transactions: AfterStatePreviewRow[] = [];

        try {

            transactions = await this.afterStatePreviewCmp.show({
                adjustmentDescriptor: side,
                positions: afterStatePositions,
                portfolio: this._selectedPortfolio,
                side: side,
                slot
            });


            // User hits "Apply State"

            // if no transactions -> return
            if (transactions.length === 0 || transactions.every(x => x.transaction.length === 0)) {
                this._toastr.info('Nothing to apply');
                return;
            }

            const otherSidePositions = this.hedgePositions
                .filter(x => !side.startsWith(x.type));

            if (transactions.every(tr => !tr.deferred && !tr.removed)) {
                // if no adjustment has been made to any transaction -> apply as is

                const afterState = transactions.flatMap(x => x.after);

                const afterState_models =
                    await this._hedgePositionsService.convertToModels(afterState);

                this.hedgePositions = afterState_models.concat(otherSidePositions);

            } else {

                // make up after state excluding deferred and remove transactions and apply it
                const netTransaction = transactions.map(tr => {
                    if (tr.deferred || tr.removed) {
                        return tr.before;
                    } else {
                        return tr.after;
                    }
                });


                const positions = netTransaction.flatMap(x => x);

                const positions_models = await this._hedgePositionsService.convertToModels(positions);

                this.hedgePositions = positions_models.concat(otherSidePositions);
            }

            // save updated current state
            const currentHedgePositions = this.hedgePositions.map(x => x.asDto());
            await this._hedgePositionsService.saveHedgePositions(currentHedgePositions, this._selectedPortfolio);


            // save updated after state
            // updated after state consists of:
            //  - hedges of deferred transactions  (as changed)
            //  - hedges of applied & cancelled transactions (as unchanged)

            if (transactions.some(tr => tr.deferred)) {

                const newAfterState = transactions.flatMap(tr => {
                    return tr.removed ? tr.before : tr.after;
                });

                this._hedgePositionsService.saveAfterState(newAfterState, slot, this._selectedPortfolio);


            } else {
                this._hedgePositionsService.clearAfterState(this._selectedPortfolio, slot);
            }


            this.refreshLegs(side);

            this._messageBus.publish({
                topic: 'Hg.AfterStateApplied',
                payload: {
                    side,
                    portfolio: this._selectedPortfolio
                }
            });

        } catch {
            //
        }
    }

    hasAfterState(slot: 'first' | 'second'): boolean {
        if (isVoid(this._selectedPortfolio)) {
            return;
        }

        const afterState = this._hedgePositionsService
            .getAfterState(slot, this._selectedPortfolio);

        return !isNullOrUndefined(afterState);
    }

    hasAnyAfterState() {
        return this.hasAfterState('first') || this.hasAfterState('second');
    }

    getAfterStateCount(): number {
        const count = (this.hasAfterState('first') ? 1 : 0)
            + (this.hasAfterState('second') ? 1 : 0);

        return count;
    }

    @DetectMethodChanges({isAsync: true})
    async clearAfterState(side?: 'Calls' | 'Puts' | 'All') {

        let sideText = 'All';

        if (!isVoid(side)) {
            sideText = side.toLowerCase();
        } else {
            if (this.hasAfterState('first')) {
                side = 'Calls';
                sideText = 'calls';
            } else {
                side = 'Puts';
                sideText = 'puts';
            }
        }

        let slot = undefined;

        if (side === 'Calls') {
            slot = 'first';
        } else if (side === 'Puts') {
            slot = 'second';
        }

        try {
            await this.confirmationDialog.show([`Are you sure you want to clear ${sideText} saved state?`]);
        } catch {
            return;
        }

        this._hedgePositionsService.clearAfterState(this._selectedPortfolio, slot);

        // Every modification discarded at once => the CoPilot resets (owner 2026-08-03). Published for
        // a single side too: its results were computed against a book that included that side's
        // modifications, so they are just as dead as after an All clear.
        this._messageBus.publish<HedgeBookResetPayload>({
            topic: HEDGE_BOOK_RESET_TOPIC,
            payload: {reason: 'saved-state-cleared'}
        });
    }

    @DetectMethodChanges({isAsync: true})
    async clearHedges() {

        try {
            await this.confirmationDialog?.show(['Are you sure you want to clear current hedges?']);
        } catch {
            return;
        }

        this.clearSavedMatrixGridState();

        this._messageBus.publish({
            topic: 'Hg.Loading',
            payload: {
                isLoading: true
            }
        });

        try {

            const oldPositions = this.hedgePositions.map(x => x.asDto());

            this.hedgePositions = [];

            await this._hedgePositionsService
                .saveHedgePositions([], this._selectedPortfolio);

            this._hedgePositionsService.clearAfterState(this._selectedPortfolio);


            this._messageBus.publish({
                topic: 'Hg.HedgesCleared',
                payload: {
                    portfolio: this._selectedPortfolio,
                    oldPositions: oldPositions
                }
            });

            // The book the CoPilot's Conversation was built on no longer exists => reset it (owner
            // 2026-08-03). Published AFTER the wipe: the CoPilot's teardown tolerates hedges that are
            // already gone (unPreviewCopilotClose returns early on a missing hedge).
            this._messageBus.publish<HedgeBookResetPayload>({
                topic: HEDGE_BOOK_RESET_TOPIC,
                payload: {reason: 'hedges-cleared'}
            });

        } finally {
            this._messageBus.publish({
                topic: 'Hg.Loading',
                payload: {
                    isLoading: false
                }
            });
        }
    }

    @DetectMethodChanges({isAsync: true})
    async resetToDefaults() {

        if (this.hedgePositions.length > 0) {
            try {
                await this.confirmationDialog?.show(
                    [
                        'Are you sure you want to clear current hedges and load default ones?'
                    ]);
            } catch {
                return;
            }
        }

        this.clearSavedMatrixGridState();

        this._messageBus.publish({
            topic: 'Hg.Loading',
            payload: {
                isLoading: true
            }
        });

        try {

            const positions = await this._apgDataService
                .getPortfolioPositions(this._selectedPortfolio);

            // Independent portfolios have no APG positions to derive the underlying from
            // (getPortfolioPositions() returns []); resolve it from the portfolio's own asset,
            // mirroring the Hedge Pad. Otherwise the default hedges are stamped with the global
            // default underlying and dropped by the Hedge Matrix's by-underlying column filter.
            // [CR-20260625-001]
            let underlying = this._selectedPortfolio?.isIndependent
                ? await this._apgDataService.getUnderlyingOfPortfolio(this._selectedPortfolio)
                : this._appSettingsService.adjustmentPricingGrid.defaultUnderlying;

            if (!isVoid(positions)) {
                if (!isVoid(positions[0])) {
                    const ticker = positions[0][0].ticker;
                    const optTicker = parseOptionTicker(ticker);
                    underlying = optTicker.underlying;
                }
            }

            const multiplier = underlying === 'SPX' ? 10 : 1;

            const callSlingshotCfg: HgOptionStrategyConfig = {
                optionType: 'Calls',
                strategy: 'Slingshot',
                strikeOffset: 5 * multiplier,
                underlying: underlying,
                strikeWidth: 1 * multiplier,
                baseQty: 10,
                color: '#3f83ea'
            };

            const callSlingshot = await this._hgOptionStrategyService
                .makeOptionStrategy(callSlingshotCfg, this._optionChainsService, this._lastQuoteCache);

            const putSlingshotCfg: HgOptionStrategyConfig = {
                optionType: 'Puts',
                strategy: 'Slingshot',
                strikeOffset: 5 * multiplier,
                underlying: underlying,
                strikeWidth: 1 * multiplier,
                baseQty: 10,
                color: '#873434'
            };

            const putSlingshot = await this._hgOptionStrategyService
                .makeOptionStrategy(putSlingshotCfg, this._optionChainsService, this._lastQuoteCache);

            this.hedgePositions = callSlingshot.concat(putSlingshot);

            const currentHedgePositions = this.hedgePositions.map(x => x.asDto());
            await this._hedgePositionsService.saveHedgePositions(
                currentHedgePositions,
                this._selectedPortfolio,
            );

            this._hedgePositionsService.clearAfterState(this._selectedPortfolio);

            this.refreshLegs('Calls & Puts');

            this._messageBus.publish({
                topic: 'Hg.InitialPositionsChanged',
                payload: {
                    portfolio: this._selectedPortfolio,
                    positions: currentHedgePositions
                }
            });

            this._messageBus.publish({
                topic: 'Hg.DefaultHedgesLoaded',
                payload: {
                    portfolio: this._selectedPortfolio,
                }
            });

            // Starting from scratch: the default hedges are a different book, so the CoPilot resets
            // (owner 2026-08-03). Hg.DefaultHedgesLoaded is not reused for this — it is one of the four
            // reset actions, and the CoPilot must not have to know which four they are.
            this._messageBus.publish<HedgeBookResetPayload>({
                topic: HEDGE_BOOK_RESET_TOPIC,
                payload: {reason: 'defaults-loaded'}
            });

        } finally {
            this._messageBus.publish({
                topic: 'Hg.Loading',
                payload: {
                    isLoading: false
                }
            });
        }
    }

    private clearSavedMatrixGridState() {
        const pfKey = makePortfolioKey(this._selectedPortfolio);
        this._userSettingsService.setValue(`hg.matrix.columns.${pfKey}`, {});
    }

    isHedgeExpired(pGroup: LegsGroup) {
        return pGroup.legs.some(x => this.isLegExpired(x));
    }

    isLegExpired(leg: HedgePositionModel): boolean {
        let isExpired = false;
        const ticker = leg.getTicker();
        if (isVoid(ticker)) {
            return !isVoid(leg.expiredTicker);
        } else {
            isExpired = this._optionChainsService.isOptionExpired(ticker);
        }
        return isExpired;
    }

    getExpiredLegExpirationText(leg: HedgePositionModel) {
        const optionTicker = parseOptionTicker(leg.expiredTicker);
        if (isVoid(optionTicker)) {
            return "EXPIRED";
        }
        let date = makeGuiFriendlyExpirationDate(optionTicker.expiration);
        date = date + ' (EXPIRED)'
        return date;
    }

    private deleteHedgeByRequest(payload: { hedgeId: string, hedgeName: string, side: 'Call' | 'Put' }) {
        const groups = this.getLegGroups(payload.side);
        if (!groups) {
            return;
        }
        const pGroup = groups.find(g => g.legs.some(leg => leg.groupId === payload.hedgeId));
        if (pGroup) {
            this.removeHedge(pGroup, payload.side);
        }
    }

    @DetectMethodChanges({isAsync: true})
    async removeHedge(pGroup: HedgeGroup, side: 'Call' | 'Put') {

        try {
            await this.confirmationDialog.show([
                `Are you sure you want to delete ${side.toLowerCase()} hedge`,
                `"${pGroup.groupLabel}"?`
            ]);
        } catch {
            return;
        }

        this.hedgePositions =
            this.hedgePositions.filter(x => pGroup.legs.indexOf(x) < 0);

        this.refreshLegs(side);

        const dtos = this.hedgePositions.map(x => x.asDto());

        await this._hedgePositionsService
            .saveHedgePositions(dtos, this._selectedPortfolio);

        this._messageBus.publish({
            topic: 'Hg.InitialPositionsChanged',
            payload: {
                portfolio: this._selectedPortfolio,
                positions: dtos
            }
        });

        this._messageBus.publish({
            topic: 'Hg.HedgeRemoved',
            payload: {
                hedgeId: pGroup.legs[0].groupId,
                side,
                portfolio: this._selectedPortfolio
            }
        });
    }

    @DetectMethodChanges({isAsync: true})
    private async removeExpiredLeg(leg: HedgePositionModel) {
        this.hedgePositions =
            this.hedgePositions.filter(x => {
                if (leg.groupId !== x.groupId) {
                    return true;
                }
                return x.expiredTicker !== leg.expiredTicker;
            });

        this.refreshLegs(leg.type);

        const dtos = this.hedgePositions.map(x => x.asDto());

        await this._hedgePositionsService
            .saveHedgePositions(dtos, this._selectedPortfolio);

        this._messageBus.publishAsync({
            topic: 'Hg.InitialPositionsChanged',
            payload: {
                portfolio: this._selectedPortfolio,
                positions: dtos
            }
        });
    }

    @DetectMethodChanges({isAsync: true})
    private async onSyncPositions(payload: { positions: HedgePositionModel[] }) {

        this._messageBus.publish({
            topic: 'Hg.Loading',
            payload: {isLoading: true}
        });

        await delay(250);

        try {

            const dtos = payload.positions
                .filter(x => !x.removed)
                .map(x => x.asDto());

            await this._hedgePositionsService.saveHedgePositions(
                dtos,
                this._selectedPortfolio,
            );

            const promises = dtos.map(x => HedgePositionModel.fromDto(x, this._optionChainsService));

            const positions = await Promise.all(promises);

            this.hedgePositions = positions;

            this.refreshLegs('Calls & Puts');

            this._messageBus.publish({
                topic: 'Hg.InitialPositionsChanged',
                payload: {
                    portfolio: this._selectedPortfolio,
                    positions: dtos
                }
            });

        } finally {

            this._messageBus.publish({
                topic: 'Hg.Loading',
                payload: {isLoading: false}
            });
        }
    }

    private refreshLegs(side: 'Call' | 'Put' | 'Calls & Puts' | 'Calls' | 'Puts') {
        if (side === 'Calls & Puts') {
            this._callLegsByGroup = undefined;
            this._putLegsByGroup = undefined;
        } else if (side.startsWith('C')) {
            this._callLegsByGroup = undefined;
        } else if (side.startsWith('P')) {
            this._putLegsByGroup = undefined;
        }
    }

    private async onRefreshClientPositions(payload: { state: "former" | "current", portfolio: ApgPortfolio }) {

        let positions: HedgePosition[];

        if (payload.state === 'former') {
            this._originalPositions.resetToSnapshotState(payload.portfolio);
        } else if (payload.state === 'current') {
            this._originalPositions.updateSnapshotStateWithCurrent(payload.portfolio);
        }

        positions = this._originalPositions.getTheirOriginalPositions(payload.portfolio);

        const promises = positions
            .map(async x => HedgePositionModel.fromDto(x, this._optionChainsService));

        const models = await Promise.all(promises);

        await this.onSyncPositions({positions: models});
    }
}
